import type {
  ChartPointAnnotationPaint,
  ChartPointMarkerType,
} from "@assetwatch/charts";
import { z } from "zod";

export const DEFAULT_TIME_RANGE_DAYS = 90;
/**
 * Upper bound on the `days` range, enforced by both the search schema below and
 * the ML API. Exported so the form can clamp to it — a larger value would be
 * dropped by the schema and silently fall back to DEFAULT_TIME_RANGE_DAYS.
 */
export const MAX_TIME_RANGE_DAYS = 365;
export const MAX_GRAPHS = 5;

/**
 * A graph key from the ML API's catalog (`get_mp_graph_types`) — e.g. `rms_ts`,
 * `temperature_athena`.
 *
 * Deliberately an open `string`, not a union: the catalog is fetched at runtime
 * and grows on the backend (RPM is next), so a closed list here would silently
 * drop new graphs. Titles, units and legend labels come from the catalog too,
 * which is why this module no longer carries any of them.
 */
export type MpGraphKey = string;

/** A key we're willing to put in a URL or send to the API. */
function isGraphKey(value: string): boolean {
  return /^[a-z0-9_]+$/i.test(value);
}

/**
 * Search-param schema for `/mp-graphs`. Everything needed to reproduce the view
 * lives in the URL so it is shareable:
 * - `mp`    external_monitoringpoint_id
 * - `graph` `,`-separated catalog keys in a single row (e.g. "peak_ts,rms_ts")
 * - `days`  time range
 * - `highlight` chart highlights: `chart:from-to[:note]` (range) or
 *   `chart:from[:note]` (marker), `;`-separated. Notes are applied from the URL.
 * - `focus` visible series per chart (e.g. "rms_ts:x,y;p2p_ts:x,y,z")
 * - `x_start`/`x_stop` the shared X viewport in Unix seconds, tracking pan/zoom
 *
 * Invalid values fall back to undefined rather than throwing.
 */
export const mpGraphSearchSchema = z.object({
  mp: z.string().trim().min(1).optional().catch(undefined),
  graph: z.string().optional().catch(undefined),
  days: z.coerce
    .number()
    .int()
    .positive()
    .max(MAX_TIME_RANGE_DAYS)
    .optional()
    .catch(undefined),
  highlight: z.string().optional().catch(undefined),
  focus: z.string().optional().catch(undefined),
  x_start: z.coerce.number().int().optional().catch(undefined),
  x_stop: z.coerce.number().int().optional().catch(undefined),
});

/**
 * The X viewport from the URL, if both bounds are present and ordered. A
 * partial or inverted pair is ignored so a hand-edited URL can't wedge the
 * charts into an empty window.
 */
export function parseViewport(
  xStart: number | undefined,
  xStop: number | undefined,
): { min: number; max: number } | undefined {
  if (xStart === undefined || xStop === undefined || xStop <= xStart) {
    return undefined;
  }
  return { min: xStart, max: xStop };
}

/**
 * Which series are visible, per chart: graph key → lowercased series labels. A
 * graph with no entry shows every series it has.
 *
 * Per-graph rather than one axis set shared across the page because the ML API's
 * `graph_focus` narrows each chart independently — one event can flag X/Y on RMS
 * and X/Y/Z on P2P — and because not every series is an axis ("Temperature").
 */
export type MpGraphFocus = Map<MpGraphKey, string[]>;

/** Labels are URL-encoded per entry, so a label may contain `,` or `;`. */
function decodeLabel(value: string): string {
  const trimmed = value.trim();
  try {
    return decodeURIComponent(trimmed).toLowerCase();
  } catch {
    return trimmed.toLowerCase();
  }
}

/**
 * Parse the `focus` param: `rms_ts:x,y;p2p_ts:x,y,z`. Entries with an unusable
 * key or no labels are dropped — an empty label list would mean a chart with no
 * lines, which is never what a link intended.
 */
export function parseFocus(value: string | undefined): MpGraphFocus {
  const focus: MpGraphFocus = new Map();

  if (!value) {
    return focus;
  }

  for (const entry of value.split(";")) {
    const [key, labelList] = entry.split(":");
    const graphKey = key?.trim();

    if (!graphKey || !isGraphKey(graphKey) || !labelList) {
      continue;
    }

    const labels = labelList.split(",").map(decodeLabel).filter(Boolean);
    if (labels.length > 0) {
      focus.set(graphKey, labels);
    }
  }

  return focus;
}

/** Serialize per-chart visibility back into the `focus` param. */
export function serializeFocus(focus: MpGraphFocus): string | undefined {
  const entries = [...focus.entries()].filter(
    ([, labels]) => labels.length > 0,
  );

  if (entries.length === 0) {
    return undefined;
  }

  return entries
    .map(
      ([key, labels]) => `${key}:${labels.map(encodeURIComponent).join(",")}`,
    )
    .join(";");
}

/**
 * Toggle one series on one chart.
 *
 * A graph with no entry is showing everything, so the first click hides only the
 * series clicked. Turning the last visible series off is ignored — an empty
 * chart is never the intent. Once every series is visible again the entry is
 * dropped, keeping the default out of the URL.
 */
export function toggleFocusLabel(
  focus: MpGraphFocus,
  graphKey: MpGraphKey,
  allLabels: string[],
  label: string,
): MpGraphFocus {
  const all = allLabels.map((value) => value.toLowerCase());
  const target = label.toLowerCase();
  const current = focus.get(graphKey) ?? all;

  const next = current.includes(target)
    ? current.filter((value) => value !== target)
    : // Rebuilt in the graph's own series order so the URL doesn't record the
      // order things were clicked in.
      all.filter((value) => current.includes(value) || value === target);

  const applied = next.length === 0 ? current : next;
  const updated = new Map(focus);

  if (applied.length >= all.length) {
    updated.delete(graphKey);
  } else {
    updated.set(graphKey, applied);
  }

  return updated;
}

/**
 * Parse the `graph` param into an ordered, de-duplicated list of catalog keys
 * for a single row of charts. Malformed tokens are dropped and the count is
 * capped at MAX_GRAPHS. Both `,` and the legacy `|` are treated as separators.
 *
 * Keys are not checked against the catalog here — that lives on the server and
 * arrives asynchronously. A key that no longer exists comes back as a graph the
 * response simply doesn't contain, which the screen renders as such.
 */
export function parseGraphs(graph: string | undefined): MpGraphKey[] {
  if (!graph) {
    return [];
  }

  const selected = new Set<MpGraphKey>();
  for (const token of graph.split(/[,|]/)) {
    const trimmed = token.trim();
    if (isGraphKey(trimmed)) {
      selected.add(trimmed);
    }
    if (selected.size >= MAX_GRAPHS) {
      break;
    }
  }

  return [...selected];
}

export function serializeGraphs(graphs: MpGraphKey[]): string {
  return graphs.join(",");
}

const MP_MARKER_SHAPES: readonly ChartPointMarkerType[] = [
  "circle",
  "square",
  "triangle",
  "cross",
  "x",
];

/**
 * The chart shape for an API marker's `shape`. The API's vocabulary matches the
 * chart's exactly; anything unrecognized falls back to a circle rather than
 * dropping the marker.
 */
export function markerShape(shape: string): ChartPointMarkerType {
  const normalized = shape.toLowerCase().trim();

  return (
    MP_MARKER_SHAPES.find((candidate) => candidate === normalized) ?? "circle"
  );
}

/**
 * Data-vis paints for markers, written as literals rather than read off the
 * package's `dataVisColorTokens`.
 *
 * That object is a runtime export of `@assetwatch/charts`, and importing it here
 * would pull the charts barrel — and SciChart's WASM with it — into every unit
 * test that touches a URL helper in this module. The literals are still checked:
 * they have to be members of `ChartPointAnnotationPaint`, so if the package
 * renames a token this stops compiling.
 *
 * `inverse` stands in for grey, which the data-vis palette doesn't have — it
 * contrasts against the chart surface, so the marker reads as a solid dark or
 * light shape depending on the theme.
 */
const MARKER_PAINTS = {
  green: "--datavis-datavis-crit-green",
  neutral: "inverse",
  red: "--datavis-datavis-crit-red",
  seriesColor: "series",
  yellow: "--datavis-datavis-crit-yellow",
} satisfies Record<string, ChartPointAnnotationPaint>;

/**
 * The chart paint for an API marker's `color`.
 *
 * Matched on substrings rather than an exact vocabulary because the backend owns
 * these names and they will drift — `positive_green` and `neutral_grey` today,
 * something else tomorrow. An unrecognized name paints the marker in its own
 * series' color, which is visible and neutral rather than a dropped marker.
 */
export function markerPaint(color: string): ChartPointAnnotationPaint {
  const normalized = color.toLowerCase();

  // Yellow is checked before red because `critical_yellow`-style names contain
  // both a hue and the word "critical".
  if (/yellow|amber|orange|warn/.test(normalized)) {
    return MARKER_PAINTS.yellow;
  }
  if (/red|negative|critical|fault|alarm/.test(normalized)) {
    return MARKER_PAINTS.red;
  }
  // Deliberately no "running" here: `not_running` contains it, and a substring
  // match would paint the off state green.
  if (/green|positive|normal/.test(normalized)) {
    return MARKER_PAINTS.green;
  }
  if (/grey|gray|neutral|idle/.test(normalized)) {
    return MARKER_PAINTS.neutral;
  }

  return MARKER_PAINTS.seriesColor;
}

export type MpChartHighlight = {
  chart: MpGraphKey;
  from: number;
  /** When set, the highlight is a shaded range; otherwise a single marker. */
  to?: number;
  /** Optional free-text note shown as the highlight's label. */
  note?: string;
};

/** Decode a URL-encoded note, tolerating malformed input. */
function decodeNote(value: string): string | undefined {
  if (!value) {
    return undefined;
  }
  try {
    return decodeURIComponent(value) || undefined;
  } catch {
    return value;
  }
}

/**
 * Parse the `highlight` param. Each `;`-separated entry is
 * `chart:from-to[:note]` (a shaded range) or `chart:from[:note]` (a marker).
 * Timestamps are unix seconds; the optional note is URL-encoded so it can carry
 * any character. Entries with an unknown chart or non-numeric time are dropped.
 */
export function parseHighlights(value: string | undefined): MpChartHighlight[] {
  if (!value) {
    return [];
  }

  return value
    .split(";")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .flatMap((entry): MpChartHighlight[] => {
      const [chart, range, ...noteParts] = entry.split(":");
      if (!chart || !range || !isGraphKey(chart)) {
        return [];
      }

      const note = decodeNote(noteParts.join(":"));
      const [fromRaw, toRaw] = range.split("-");
      const from = Number(fromRaw);
      if (!Number.isFinite(from)) {
        return [];
      }

      if (toRaw === undefined) {
        return [{ chart, from, note }];
      }

      const to = Number(toRaw);
      return Number.isFinite(to)
        ? [{ chart, from, to, note }]
        : [{ chart, from, note }];
    });
}
/**
 * Serialize a single highlight into a URL-encoded string component.
 * Format: `chart:from-to[:note]` (range) or `chart:from[:note]` (marker).
 */
export function serializeHighlight(highlight: MpChartHighlight): string {
  const range =
    highlight.to !== undefined
      ? `${highlight.from}-${highlight.to}`
      : String(highlight.from);
  if (!highlight.note) {
    return `${highlight.chart}:${range}`;
  }
  return `${highlight.chart}:${range}:${encodeURIComponent(highlight.note)}`;
}

/**
 * Serialize an array of highlights into the `highlight` URL param.
 * Returns undefined if the array is empty (to keep URLs clean).
 */
export function serializeHighlights(
  highlights: MpChartHighlight[],
): string | undefined {
  if (highlights.length === 0) {
    return undefined;
  }
  return highlights.map(serializeHighlight).join(";");
}
